import Head from "next/head";

import {
  DEFAULT_OG_IMAGE,
  FAVICON_URL,
  SITE_NAME,
  SITE_ORIGIN,
} from "./constants";
import { absoluteUrl, type RobotsDirective } from "./indexing";

export type PageMetaProps = {
  title: string;
  description: string;
  /** Path only, e.g. `/support/faq`. Defaults to no canonical when omitted. */
  canonicalPath?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  robots?: RobotsDirective;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** Extra Open Graph audio URL (voice preview). */
  ogAudio?: string;
  siteName?: string;
  /** When false, skip favicon link (Navigation already set it). Default true. */
  includeFavicon?: boolean;
};

const PageMeta: React.FC<PageMetaProps> = ({
  title,
  description,
  canonicalPath,
  ogImage = DEFAULT_OG_IMAGE,
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  robots = "index,follow",
  jsonLd,
  ogAudio,
  siteName = SITE_NAME,
  includeFavicon = true,
}) => {
  const canonical =
    canonicalPath !== undefined ? absoluteUrl(canonicalPath) : undefined;
  const ogUrl = canonical ?? SITE_ORIGIN;
  const imageType = ogImage.includes(".")
    ? `image/${ogImage.split(".").pop()?.split("?")[0] ?? "jpeg"}`
    : "image/jpeg";

  const jsonLdBlocks = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      {canonical ? <link rel="canonical" href={canonical} /> : null}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={ogUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:type" content={imageType} />
      {ogImageAlt ? (
        <meta property="og:image:alt" content={ogImageAlt} />
      ) : null}
      {ogImageWidth ? (
        <meta property="og:image:width" content={String(ogImageWidth)} />
      ) : null}
      {ogImageHeight ? (
        <meta property="og:image:height" content={String(ogImageHeight)} />
      ) : null}
      {ogAudio ? (
        <>
          <meta property="og:audio" content={ogAudio} />
          <meta property="og:audio:secure_url" content={ogAudio} />
          <meta property="og:audio:type" content="audio/mpeg" />
        </>
      ) : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {includeFavicon ? (
        <link rel="icon" href={FAVICON_URL} sizes="any" />
      ) : null}

      {jsonLdBlocks.map((block, index) => (
        <script
          // Stable order; content is our own structured data
          key={`json-ld-${index}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
        />
      ))}
    </Head>
  );
};

export default PageMeta;
